/**
 * Otto Agent — the app-wide agentic controller.
 *
 * Mounted once in `AppShell` (mirroring `CallProvider`). It owns:
 *   - the conversation transcript (the gateway is stateless — we replay it),
 *   - the client-side data-tool loop (executes CRM reads via `@crm-src` so the
 *     financial / view-scope walls hold),
 *   - the streamed "thinking" feed the cloak renders,
 *   - applying the terminal action (navigate + pre-fill the creation form).
 *
 * Otto never commits: the loop ends by OPENING a pre-filled form; the human
 * reviews and clicks Save/Send. "Stop" aborts instantly and hands control back
 * (nothing was saved).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import type {
  OttoActionEvent,
  OttoAgentSkillId,
  OttoContentBlock,
  OttoToolCallEvent,
  OttoTranscript,
} from '@xop/dashboard-shared';
import { ensureCrmSession, runOttoDataTool } from '@crm-src/lib/otto-tools';

import { toast } from '../components/ui/use-toast.js';
import { ottoOriginFromBase } from '../lib/otto/tokenRefresh.js';
import { routeOttoMessage } from './routeOttoMessage.js';
import { applyOttoAction } from './actions.js';
import { streamOttoAct } from './streamOttoAct.js';
import { useAgenticEnabled } from './useAgenticEnabled.js';

/** Cloak phase / run state. */
export type AgenticStatus =
  | 'idle'
  | 'thinking' // reasoning + running data tools
  | 'awaiting-input' // asked the user a clarifying question
  | 'handoff' // applied the action; the form is opening (cloak fades)
  | 'error';

/** One entry in the live thinking feed. */
export type AgentEntry =
  | { kind: 'thinking'; text: string }
  | { kind: 'step'; label: string; status: 'start' | 'done' | 'error' }
  | { kind: 'tool'; id: string; label: string; status: 'running' | 'done' | 'error' }
  | { kind: 'note'; text: string };

export interface AgenticContextValue {
  status: AgenticStatus;
  /** True whenever the cloak should be up (status !== 'idle'). */
  active: boolean;
  entries: AgentEntry[];
  /** The clarifying question when `status === 'awaiting-input'`. */
  question: string | null;
  error: string | null;
  /** Begin a run from a fresh prompt. */
  start: (prompt: string) => void;
  /** Answer a clarifying question and continue. */
  reply: (text: string) => void;
  /** Abort and hand control back. */
  stop: () => void;
  /** The command composer (the "ask Otto" dialog) — driven from the context so
   * the FAB, ⌘K, AND contextual "Create with AI" buttons all open the same one. */
  composerOpen: boolean;
  /** A skill to pre-scope the composer to (seeds an example), or null. */
  composerSkill: OttoAgentSkillId | null;
  /** Open the composer, optionally pre-scoped to a skill (ignored if the user
   * can't run it). No-op when the agent surface is disabled for this user. */
  openComposer: (skill?: OttoAgentSkillId) => void;
  closeComposer: () => void;
}

const AgenticContext = createContext<AgenticContextValue | null>(null);

/** Max client-driven turns before we bail (a runaway safety bound). */
const MAX_TURNS = 12;
/** How long the translucent cloak lingers after the form opens. */
const HANDOFF_LINGER_MS = 1600;

function toolLabel(name: OttoToolCallEvent['name'], input: Record<string, unknown>): string {
  const q = typeof input.query === 'string' ? input.query : '';
  switch (name) {
    case 'find_customer':
      return `Looking up customer “${q}”`;
    case 'list_customer_contacts':
      return 'Fetching the customer’s contacts';
    case 'get_customer_deals':
      return 'Checking the customer’s deals';
    case 'search_products':
      return `Searching products for “${q}”`;
    case 'list_contact_groups':
      return 'Loading contact groups';
    case 'search_contacts':
      return `Searching contacts for “${q}”`;
    default:
      return `Running ${name}`;
  }
}

export function AgenticProvider({ children }: { children: ReactNode }): JSX.Element {
  const cap = useAgenticEnabled();
  const navigate = useNavigate();

  const [status, setStatus] = useState<AgenticStatus>('idle');
  const [entries, setEntries] = useState<AgentEntry[]>([]);
  const [question, setQuestion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerSkill, setComposerSkill] = useState<OttoAgentSkillId | null>(null);

  // Mutable run state (kept in refs so the async loop never reads stale values).
  const transcriptRef = useRef<OttoTranscript>([]);
  const abortRef = useRef<AbortController | null>(null);
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const capRef = useRef(cap);
  capRef.current = cap;
  const navRef = useRef(navigate);
  navRef.current = navigate;

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (releaseTimer.current) clearTimeout(releaseTimer.current);
    },
    [],
  );

  // ── thinking-feed mutators ───────────────────────────────────────────────
  const appendThinking = useCallback((text: string) => {
    setEntries((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.kind === 'thinking') {
        return [...prev.slice(0, -1), { kind: 'thinking', text: last.text + text }];
      }
      return [...prev, { kind: 'thinking', text }];
    });
  }, []);
  const pushEntry = useCallback((entry: AgentEntry) => {
    setEntries((prev) => [...prev, entry]);
  }, []);
  const setToolStatus = useCallback((id: string, s: 'done' | 'error') => {
    setEntries((prev) =>
      prev.map((e) => (e.kind === 'tool' && e.id === id ? { ...e, status: s } : e)),
    );
  }, []);

  const scheduleRelease = useCallback(() => {
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    releaseTimer.current = setTimeout(() => {
      setStatus('idle');
      toast({
        title: 'Otto handed it back',
        description: 'Review the pre-filled draft and Save when you’re ready.',
      });
    }, HANDOFF_LINGER_MS);
  }, []);

  // ── the turn loop ────────────────────────────────────────────────────────
  const runTurns = useCallback(async () => {
    const signal = abortRef.current?.signal;
    for (let turn = 0; turn < MAX_TURNS; turn++) {
      if (signal?.aborted) return;

      let assistantBlocks: OttoContentBlock[] | null = null;
      const toolCalls: OttoToolCallEvent[] = [];
      let action: OttoActionEvent | null = null;
      let message: string | null = null;
      let failed: string | null = null;

      await streamOttoAct(
        {
          transcript: transcriptRef.current,
          enabled_skills: capRef.current.skills,
          actor: capRef.current.actor,
        },
        {
          onEvent: (e) => {
            switch (e.type) {
              case 'thinking':
                appendThinking(e.text);
                break;
              case 'step':
                pushEntry({ kind: 'step', label: e.label, status: e.status });
                break;
              case 'assistant':
                assistantBlocks = e.blocks;
                break;
              case 'tool_call':
                toolCalls.push(e);
                pushEntry({ kind: 'tool', id: e.id, label: toolLabel(e.name, e.input), status: 'running' });
                break;
              case 'action':
                action = e;
                break;
              case 'message':
                message = e.text;
                break;
              case 'error':
                failed = e.message;
                break;
              case 'done':
                break;
            }
          },
        },
        signal,
      );

      if (signal?.aborted) return;
      if (failed) {
        setError(failed);
        setStatus('error');
        return;
      }

      // Carry the assistant's tool_use turn into the transcript before we reply
      // with the tool results (Anthropic requires the pairing).
      if (assistantBlocks) {
        transcriptRef.current = [
          ...transcriptRef.current,
          { role: 'assistant', content: assistantBlocks },
        ];
      }

      if (action) {
        setStatus('handoff');
        try {
          const applied = applyOttoAction(action, navRef.current);
          pushEntry({ kind: 'note', text: applied.label });
          scheduleRelease();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Otto produced an invalid draft.');
          setStatus('error');
        }
        return;
      }

      if (toolCalls.length > 0) {
        const results: OttoContentBlock[] = [];
        for (const tc of toolCalls) {
          try {
            const result = await runOttoDataTool(tc.name, tc.input);
            results.push({ type: 'tool_result', tool_use_id: tc.id, content: JSON.stringify(result) });
            setToolStatus(tc.id, 'done');
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            results.push({ type: 'tool_result', tool_use_id: tc.id, content: JSON.stringify({ error: msg }) });
            setToolStatus(tc.id, 'error');
          }
        }
        transcriptRef.current = [...transcriptRef.current, { role: 'user', content: results }];
        continue; // re-invoke the gateway with the results
      }

      if (message != null) {
        transcriptRef.current = [
          ...transcriptRef.current,
          { role: 'assistant', content: [{ type: 'text', text: message }] },
        ];
        // A plain-text turn streams its text as `thinking` too, so the same
        // words would show BOTH in the feed and in the question box. Drop the
        // trailing thinking entries — they ARE this message — and show it once.
        setEntries((prev) => {
          let end = prev.length;
          while (end > 0 && prev[end - 1]!.kind === 'thinking') end -= 1;
          return prev.slice(0, end);
        });
        setQuestion(message);
        setStatus('awaiting-input');
        return;
      }

      // Turn produced nothing to act on — treat as a graceful end.
      setStatus('idle');
      return;
    }

    setError('Otto took too many steps. Try a more specific request.');
    setStatus('error');
  }, [appendThinking, pushEntry, setToolStatus, scheduleRelease]);

  const start = useCallback(
    (prompt: string) => {
      const text = prompt.trim();
      if (!text || !capRef.current.enabled) return;
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      if (releaseTimer.current) clearTimeout(releaseTimer.current);
      transcriptRef.current = [{ role: 'user', content: [{ type: 'text', text }] }];
      setEntries([]);
      setQuestion(null);
      setError(null);
      setStatus('thinking');
      // Mint the CRM session up front (this surface is outside /crm). Best-effort:
      // a failure still lets tool calls self-heal on their first 401.
      void ensureCrmSession()
        .catch(() => undefined)
        .then(() => runTurns());
    },
    [runTurns],
  );

  const reply = useCallback(
    (text: string) => {
      const t = text.trim();
      if (!t) return;
      transcriptRef.current = [
        ...transcriptRef.current,
        { role: 'user', content: [{ type: 'text', text: t }] },
      ];
      setQuestion(null);
      setError(null);
      setStatus('thinking');
      void runTurns();
    },
    [runTurns],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    setStatus('idle');
    setQuestion(null);
  }, []);

  const openComposer = useCallback((skill?: OttoAgentSkillId) => {
    if (!capRef.current.enabled) return;
    // Only honour a preselected skill the user may actually run.
    setComposerSkill(skill && capRef.current.skills.includes(skill) ? skill : null);
    setComposerOpen(true);
  }, []);
  const closeComposer = useCallback(() => setComposerOpen(false), []);

  // Bridge from the embedded Staff Otto widget. This is how the agent lives
  // "inside Otto Staff": the widget triggers, the DASHBOARD runs it (cloak +
  // form pre-fill). Origin-gated to the Otto host; the customer portal never
  // mounts this provider, and the customer widget never sets the `data-agentic`
  // flag — so it stays staff-only.
  //
  // Two messages:
  //   `otto:agent-route` — "is this agent work?" The widget can't know (it used
  //     to guess with keyword regexes), so we ask the model and answer back;
  //     only then does the cloak come up. See docs/otto-agent.md.
  //   `otto:agent-run` — run this, no questions. Still honoured: it's what ⌘K
  //     and the "Create with AI" buttons use, and an older cached widget may
  //     still post it.
  useEffect(() => {
    const viteEnv = (import.meta as { env?: Record<string, string> }).env ?? {};
    const ottoOrigin = ottoOriginFromBase(viteEnv.VITE_OTTO_BASE_URL);
    const onMessage = (e: MessageEvent) => {
      if (ottoOrigin && e.origin !== ottoOrigin) return;
      const data = e.data as
        | { type?: string; text?: unknown; id?: unknown; context?: unknown }
        | null;
      if (!data) return;

      if (data.type === 'otto:agent-route') {
        const text = typeof data.text === 'string' ? data.text.trim() : '';
        const id = typeof data.id === 'string' ? data.id : '';
        // Answer the widget FIRST so it can render, then take the screen.
        const answer = (handled: boolean) => {
          try {
            (e.source as Window | null)?.postMessage(
              { type: 'otto:agent-route-result', id, handled },
              ottoOrigin || '*',
            );
          } catch {
            /* frame closed mid-flight */
          }
        };
        if (!text || capRef.current.skills.length === 0) {
          answer(false); // nothing this user can run ⇒ never an action
          return;
        }
        const context = Array.isArray(data.context)
          ? data.context.filter((l): l is string => typeof l === 'string').slice(-20)
          : [];
        void routeOttoMessage({
          message: text,
          enabled_skills: capRef.current.skills,
          context,
        })
          .then((decision) => {
            const handled = !!decision.skill;
            answer(handled);
            if (handled) start(text);
          })
          .catch(() => answer(false)); // fail safe: let Otto answer
        return;
      }

      if (data.type !== 'otto:agent-run') return;
      const text = typeof data.text === 'string' ? data.text.trim() : '';
      if (text) start(text);
      else openComposer();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // start/openComposer are stable useCallbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, openComposer]);

  const value = useMemo<AgenticContextValue>(
    () => ({
      status,
      active: status !== 'idle',
      entries,
      question,
      error,
      start,
      reply,
      stop,
      composerOpen,
      composerSkill,
      openComposer,
      closeComposer,
    }),
    [status, entries, question, error, start, reply, stop, composerOpen, composerSkill, openComposer, closeComposer],
  );

  return <AgenticContext.Provider value={value}>{children}</AgenticContext.Provider>;
}

export function useAgentic(): AgenticContextValue {
  const ctx = useContext(AgenticContext);
  if (!ctx) throw new Error('useAgentic must be used within <AgenticProvider>');
  return ctx;
}

/** Non-throwing variant — null when there is no `<AgenticProvider>` above. Lets
 * a drop-in button (rendered on CRM pages that unit-test in isolation) no-op
 * instead of crashing the page. */
export function useAgenticOptional(): AgenticContextValue | null {
  return useContext(AgenticContext);
}
